import _ from 'lodash'
import keys from '@ps/permission/keys'
import store from '../store'
import iframe from '@ps/bridge/iframe/iframe'
import * as bgScript from '@ps/bridge/qc/scripts/bg-config'
import { workbenchRoute } from '@ps/router/config'
import { hasPermission } from '@ps/plugins/permission-manager/manager'
import { checkIsSupportBox2d } from '@ps/store/workbench/visual-program/utils'
import { isIvPage } from '@ps/router/config'

// project：制作项目；prefab：编辑 prefab
type PageMode = 'edit-project' | 'edit-prefab'
type PermissionItem = {
  validator: () => boolean
  key: string | string[]
}

const { assetTypes, pageModes } = workbenchRoute

const isEditProject = (mode: string): boolean => mode === pageModes.editProject
const isEditPrefab = (mode: string): boolean => mode === pageModes.editPrefab
const isEditGlobal = (mode: string): boolean =>
  isEditProject(mode) || isEditPrefab(mode)

const recordPrefabImage = (mode: string, assetType: string) =>
  isEditPrefab(mode) && assetType === assetTypes.particle
const recordDragonImage = (mode: string, assetType: string) =>
  isEditPrefab(mode) && assetType === assetTypes.dragonBone
const capturePrefabImage = (mode: string, assetType: string) =>
  isEditPrefab(mode) && assetType === assetTypes.scene
const globalConfigIsNormal = () => !!bgScript.getScript(iframe.gamePlay)

// 内部自研素材
export const hasSelfDevPermission = () => {
  return (
    hasPermission(keys.selfDev) &&
    iframe.qici.config.basicTemplate === 'selfDev'
  )
}
// 协同流程制作的项目
export const isCooperateProject = () => {
  return iframe.qici.config.basicTemplate === 'selfDev'
}
// 刚体参数设置
export const hasBox2dPermission = () => {
  const isBlankProject = store.state.workspace.project.isBlankProject
  const enableRigidBody = store.state.workspace.project.enableRigidBody
  // 内部用户的所有项目或者外部用户用支持开启刚体参数的模版创建的项目
  return (
    checkIsSupportBox2d() &&
    (hasPermission(keys.box2d) ||
      (!hasPermission(keys.box2d) && !isBlankProject && enableRigidBody))
  )
}

// eslint-disable-next-line space-before-function-paren
export default function ({
  // 当前页面的模式
  mode,
  // 当前页面的资源类型为 edit-prefab 时，要编辑的预设的类型
  assetType,
  // 默认使用 false
  isNormMaterial = false,
}: {
  mode: PageMode
  assetType: string
  isNormMaterial?: boolean
}): string[] {
  // 是否是内部账号
  const isInternalAccount = store.state.user.info.is_internal_account
  const isLoginFromWh = store.state.user.info.is_login_from_wh
  const isBlankProject = store.state.workspace.project.isBlankProject
  const isUserTemplateProject =
    store.state.workspace.project.isUserTemplateProject
  const isShowSaveBtn = true

  // 非标准包，即不是使用新版 ps 制作的原始包
  const isNonNormMaterial = !isNormMaterial
  // 是否支持玩法模板
  const canPlayUnit = true

  // 只限制 UI 的权限 key
  const uiRoles: PermissionItem[] = [
    // 导航条
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.navbar,
    },
    // 全局设置
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.globalSetting,
    },
    // 场景设置
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.sceneSetting,
    },
    // 事件设置
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.eventSetting,
    },
    // 素材包语言选择组件
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.languageSelector,
    },
    // 素材包横竖屏切换组件
    {
      validator() {
        return isEditGlobal(mode)
      },
      key: keys.orientationSelector,
    },
    // 播放场景
    {
      validator() {
        return isEditProject(mode) && isNormMaterial
      },
      key: keys.playScene,
    },
    // 添加组
    {
      validator() {
        return isEditProject(mode) || recordPrefabImage(mode, assetType)
      },
      key: keys.addGroup,
    },
    // 切换图层组件
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.toggleLayer,
    },
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.viewSwitcher,
    },
    // 预估包体大小组件
    {
      validator() {
        return isEditProject(mode)
      },
      key: keys.estimateSize,
    },
    // 录制预览 gif 图组件
    {
      validator() {
        return (
          recordPrefabImage(mode, assetType) ||
          recordDragonImage(mode, assetType)
        )
      },
      key: keys.recordPrefabImage,
    },
    // 生成预览图组件
    {
      validator() {
        return capturePrefabImage(mode, assetType)
      },
      key: keys.capturePrefabImage,
    },
    // 保存粒子
    {
      validator() {
        return (
          isShowSaveBtn &&
          isInternalAccount &&
          isLoginFromWh &&
          (recordPrefabImage(mode, assetType) ||
            capturePrefabImage(mode, assetType) ||
            recordDragonImage(mode, assetType))
        )
      },
      key: keys.savePrefabImage,
    },
    // 下载预设
    {
      validator() {
        return (
          isInternalAccount &&
          (recordPrefabImage(mode, assetType) ||
            capturePrefabImage(mode, assetType))
        )
      },
      key: keys.downloadPrefab,
    },
    // 粒子编辑页添加资产库的粒子
    {
      validator() {
        return isInternalAccount && recordPrefabImage(mode, assetType)
      },
      key: keys.prefabAddParticle,
    },
    {
      validator() {
        return store.state.user.info.is_internal_account
      },
      key: keys.createPrefab,
    },
    {
      validator() {
        // 制作页有编辑文本权限，编辑预设页没有编辑文本权限
        return isEditProject(mode)
      },
      key: keys.editTextValue,
    },
    {
      validator() {
        // 制作页有编辑文本权限，编辑预设页没有编辑文本权限
        return isEditProject(mode) || recordPrefabImage(mode, assetType)
      },
      key: keys.addNode,
    },
    // 故事版
    {
      validator() {
        // return isNormMaterial
        // 暂时隐藏故事版
        return false
      },
      key: keys.storyBoard,
    },
    // 玩法模板内部功能
    {
      validator() {
        return isInternalAccount
      },
      key: keys.playUnitInner,
    },
    // 粘贴动画
    {
      validator() {
        return isNormMaterial
      },
      key: keys.pasteAnimation,
    },
    // 编辑动画
    {
      validator() {
        return isNormMaterial
      },
      key: keys.editAnimation,
    },
    // 自研素材
    {
      validator() {
        return isInternalAccount
      },
      key: keys.selfDev,
    },
    // 自定义事件
    {
      validator() {
        return isInternalAccount
      },
      key: keys.customEvent,
    },
    // 刚体参数设置
    {
      validator() {
        return isInternalAccount
      },
      key: keys.box2d,
    },
    // 配对类别编辑弹窗
    {
      validator() {
        return isInternalAccount || isBlankProject || isUserTemplateProject
      },
      key: keys.editPairCategory,
    },
    // 新增配对关系按钮
    {
      validator() {
        return isInternalAccount || isBlankProject || isUserTemplateProject
      },
      key: keys.addPairRelation,
    },
  ]

  const resourceCategoryRoles = [
    // 添加图片
    {
      validator() {
        return true
      },
      key: keys.categoryImage,
    },
    // 添加视频节点
    {
      validator() {
        return isNormMaterial
      },
      key: keys.categoryVideo,
    },
    // 添加音频节点
    {
      validator() {
        return isNormMaterial
      },
      key: keys.categoryAudio,
    },
    // 添加背景音乐
    {
      validator() {
        return true
      },
      key: keys.categoryBgm,
    },
    // 添加文本
    {
      validator() {
        return true
      },
      key: keys.categoryText,
    },
    // 互动视频、互动元件
    {
      validator() {
        return isIvPage()
      },
      key: keys.categoryIvUnit,
    },
    // 添加特效
    {
      validator() {
        return true
      },
      key: keys.categoryEffect,
    },
    // 添加空节点
    {
      validator() {
        return true
      },
      key: keys.categoryEmptyNode,
    },
    // 添加预设场景
    {
      validator() {
        return isNormMaterial && !isIvPage()
      },
      key: keys.categoryScene,
    },
    // 添加玩法模板
    {
      validator() {
        return canPlayUnit
      },
      key: keys.categoryPlayUnit,
    },
    // 添加快捷布局
    {
      validator() {
        return canPlayUnit && isInternalAccount
      },
      key: keys.categoryQuickLayout,
    },
  ]

  const styleRoles = [
    {
      validator() {
        return isNormMaterial
      },
      key: keys.styleDeleteLine,
    },
    {
      validator() {
        return isNormMaterial
      },
      key: keys.styleLetterSpacing,
    },
    {
      validator() {
        return isNormMaterial
      },
      key: keys.styleUnderline,
    },
    {
      validator() {
        return isNormMaterial
      },
      key: keys.styleLineHeight,
    },
  ]

  const gameConfigRoles = [
    // 动态参数
    {
      validator() {
        // 暂时用这个做权限判断
        return true
      },
      key: keys.gameConfigSetting,
    },
    {
      validator() {
        return isInternalAccount
      },
      key: keys.gameConfigTitleEditable,
    },

    {
      validator() {
        return isInternalAccount
      },
      key: keys.gameConfigDescEditable,
    },

    {
      validator() {
        return isInternalAccount
      },
      key: keys.gameConfigSliderScopeEditable,
    },

    {
      validator() {
        return isInternalAccount
      },
      key: keys.editEventName,
    },
  ]

  const playComponentRoles = [
    {
      validator() {
        return isInternalAccount
      },
      key: keys.playComponentAddElement,
    },
  ]

  const roles: PermissionItem[] = [
    ...uiRoles,
    ...styleRoles,
    ...gameConfigRoles,
    ...resourceCategoryRoles,
    ...playComponentRoles,
  ]
  return _.flattenDeep(
    roles.filter((role) => role.validator()).map((role) => role.key),
  )
}
